import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AdminSidebar from "./AdminSidebar";
import AdminTopBar from "./AdminTopBar";
import api from "./api/axios";
import { useActivityLogger } from "./hooks/useActivity";
import { getImageUrl } from "./utils/image";

export default function AdminEditUserDashboard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { logAdminActivity } = useActivityLogger();

  const [user, setUser] = useState(null);
  const [isVerified, setIsVerified] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Validate ID before useEffect
  if (!id || isNaN(id) || parseInt(id) <= 0) {
    return (
      <div className="min-h-screen flex">
        <AdminSidebar />
        <div className="flex-1 p-6 bg-gray-50">
          <AdminTopBar />
          <div className="bg-white p-6 rounded shadow">
            <button
              onClick={() => navigate(-1)}
              className="text-xl text-black font-semibold mb-8"
            >
              &lt; Edit User
            </button>
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700 text-sm">ID user tidak valid</p>
            </div>
            <button
              onClick={() => navigate("/admin/users")}
              className="bg-gray-500 text-white px-6 py-2 rounded hover:bg-gray-600 transition"
            >
              Kembali ke Daftar User
            </button>
          </div>
        </div>
      </div>
    );
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    fetchUser();
    fetchRoles();
    // REMOVE fetchUserRoleDisplay(id); - This is causing the error!
  }, [id]);


  const fetchUserRoles = async (userId) => {
    try {
      // FIX: correct endpoint path (public)
      const response = await api.get(`/users/${userId}/roles`);
      return response.data || [];
    } catch (error) {
      console.error("Error fetching user roles:", error);
      return [];
    }
  };

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    const testApiResponse = async () => {
      try {
        const response = await api.get(`/admins/users/${id}`);

      } catch (error) {
        console.error("API test failed:", error);
      }
    };

    if (id) {
      testApiResponse();
    }
  }, [id]);

  const fetchUser = async () => {
    try {
      const response = await api.get(`/admins/users/${id}`);
      const userData = response.data;


      setUser(userData);
      setIsVerified(userData.is_verified || false);

      // Prefer roles from user payload, fallback to separate endpoint
      if (userData.roles && Array.isArray(userData.roles) && userData.roles.length > 0) {
        const roleIds = userData.roles
          .map((role) => role.ID || role.id || role.role_id || role.RoleID)
          .filter((rid) => rid !== undefined && rid !== null);
        setSelectedRoles(roleIds);
      } else {
        console.log("No roles in user payload. Fetching roles via /users/:id/roles...");
        const userRoles = await fetchUserRoles(id);
        const roleIds = (Array.isArray(userRoles) ? userRoles : [])
          .map((role) => role.ID || role.id || role.role_id || role.RoleID)
          .filter((rid) => rid !== undefined && rid !== null);
        console.log("Fallback role IDs:", roleIds);
        setSelectedRoles(roleIds);
      }

      logAdminActivity(
        "view",
        "view_edit_user",
        "user",
        userData.ID || userData.id,
        userData.username,
        "success",
        "",
        `Admin viewed edit user page for ${userData.username}`
      );
    } catch (error) {
      console.error("Error fetching user:", error);
      if (error.response?.status === 400) {
        setErrorMessage("ID user tidak valid");
      } else if (error.response?.status === 404) {
        setErrorMessage("User tidak ditemukan");
      } else {
        setErrorMessage("Gagal memuat data user");
      }
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await api.get("/roles");
      setAvailableRoles(response.data || []);
    } catch (error) {
      console.error("Error fetching roles:", error);
      setErrorMessage("Gagal memuat data roles");
    }
  };

  // WORKING role toggle function
  const toggleRole = (roleId) => {

    if (roleId === undefined || roleId === null) {
      console.error("Invalid roleId:", roleId);
      return;
    }

    // Ensure consistent type comparison (convert to number if needed)
    const numericRoleId =
      typeof roleId === "string" ? parseInt(roleId) : roleId;
    const numericSelectedRoles = selectedRoles.map((id) =>
      typeof id === "string" ? parseInt(id) : id
    );

    setSelectedRoles((prevRoles) => {
      const numericPrevRoles = prevRoles.map((id) =>
        typeof id === "string" ? parseInt(id) : id
      );

      if (numericPrevRoles.includes(numericRoleId)) {
        console.log("Removing role:", numericRoleId);
        return numericPrevRoles.filter((id) => id !== numericRoleId);
      } else {
        console.log("Adding role:", numericRoleId);
        return [...numericPrevRoles, numericRoleId];
      }
    });
  };

  const handleSave = async () => {
    setLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const updateData = {
        is_verified: isVerified,
        role_ids: selectedRoles, // Array of role IDs
      };

      const response = await api.put(`/admins/users/${id}`, updateData);
      console.log("Update response:", response.data);
      setSuccessMessage("User berhasil diupdate");

      logAdminActivity(
        "update",
        "update_user_roles",
        "user",
        parseInt(id),
        user?.username || "",
        "success",
        "",
        `Admin updated user - Verified: ${isVerified}, Roles: [${selectedRoles.join(", ")}]`
      );

    } catch (error) {

      const errorMsg = error.response?.data?.error || "Gagal mengupdate user";
      setErrorMessage(errorMsg);

      logAdminActivity(
        "update",
        "update_user_roles",
        "user",
        parseInt(id),
        user?.username || "",
        "failed",
        errorMsg,
        "Failed to update user roles"
      );
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex">
        <AdminSidebar />
        <div className="flex-1 p-6 bg-gray-50">
          <AdminTopBar />
          <div className="bg-white p-6 rounded shadow">
            <div className="flex justify-center items-center h-64">
              <p>Memuat data user...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      <AdminSidebar />
      <div className="flex-1 p-6 bg-gray-50">
        <AdminTopBar />
        <div className="bg-white p-6 rounded shadow">
          <button
            onClick={() => navigate(-1)}
            className="text-xl text-black font-semibold mb-8"
          >
            &lt; Edit User
          </button>

          {/* Messages */}
          {errorMessage && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700 text-sm">{errorMessage}</p>
            </div>
          )}

          {successMessage && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-700 text-sm">{successMessage}</p>
            </div>
          )}

          {/* User Info Display */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h2 className="text-lg font-semibold mb-4">Informasi User</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium">Username:</span>
                <p className="text-gray-700">{user.username}</p>
              </div>

              <div>
                <span className="font-medium">Nama:</span>
                <p className="text-gray-700">
                  {user.nama_ktp || user.full_name || "-"}
                </p>
              </div>


              <div>
                <span className="font-medium">Nama Samaran:</span>
                <p className="text-gray-700">
                  {user.nama_samaran || user.pseudonym || "-"}
                </p>
              </div>
              {user.image_url && (
                <div className="md:col-span-2">
                  <span className="font-medium">Foto Profil:</span>
                  <div className="mt-2">
                    <img
                      src={getImageUrl(user.image_url)}
                      alt="Profile"
                      className="h-20 w-20 rounded-full object-cover border"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Edit Form */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Verification Status */}
            <div>
              <label className="text-sm font-medium mb-2 block">
                Status Verifikasi
              </label>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isVerified}
                    onChange={(e) => setIsVerified(e.target.checked)}
                    className="form-checkbox h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                    disabled={loading}
                  />
                  <span className="text-sm">User terverifikasi</span>
                </label>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                User yang terverifikasi memiliki akses penuh ke sistem
              </p>
            </div>

            {/* WORKING Roles Section */}
            <div>
              <label className="text-sm font-medium mb-2 block">
                Roles{" "}
                <span className="text-gray-500">
                  ({selectedRoles.length} dipilih)
                </span>
              </label>

              <div className="space-y-2 max-h-60 overflow-y-auto border rounded-lg p-3">
                {availableRoles.length > 0 ? (
                  availableRoles.map((role) => {
                    // Handle both ID and id properties
                    const roleId = role.ID || role.id;

                    if (!roleId) {
                      console.error("Role missing ID:", role);
                      return null;
                    }

                    // Ensure consistent type comparison
                    const numericRoleId =
                      typeof roleId === "string" ? parseInt(roleId) : roleId;
                    const numericSelectedRoles = selectedRoles.map((id) =>
                      typeof id === "string" ? parseInt(id) : id
                    );
                    const isSelected =
                      numericSelectedRoles.includes(numericRoleId);


                    return (
                      <label
                        key={roleId}
                        className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {

                            toggleRole(roleId);
                          }}
                          className="form-checkbox h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                          disabled={loading}
                        />
                        <span className="text-sm flex-1">
                          {role.name || "Unnamed Role"}
                        </span>
                        <span className="text-xs text-gray-400">
                          ID: {numericRoleId} | Selected:{" "}
                          {isSelected ? "YES" : "NO"}
                        </span>
                        {!role.is_editable && (
                          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                            System
                          </span>
                        )}
                      </label>
                    );
                  })
                ) : (
                  <p className="text-sm text-gray-500">Memuat roles...</p>
                )}
              </div>

              <p className="text-xs text-gray-500 mt-1">
                Klik checkbox untuk menambah/menghapus role dari user
              </p>
            </div>
          </div>


          {/* Save Button */}
          <div className="mt-8 flex justify-end space-x-4">
            <button
              onClick={() => navigate(-1)}
              className="bg-gray-500 text-white px-6 py-2 rounded hover:bg-gray-600 transition"
              disabled={loading}
            >
              Batal
            </button>
            <button
              onClick={handleSave}
              className="bg-blue-500 text-white px-6 py-2 rounded hover:bg-blue-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? "Menyimpan..." : "Simpan Perubahan"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
